import { useState } from "react";
import {
  ArrowRight, CheckCircle2, Globe2, Sparkles, MapPin, Phone, Mail,
  Images, ShieldCheck, Users, Briefcase, Palette, Camera, Monitor,
  Zap, Star, Clock, DollarSign,
} from "lucide-react";import { motion } from "framer-motion";

/**
 * ExpoStand Freelancer Landing Page
 * - Kırmızı renk teması (Contractor = yeşil, Supplier = turuncu)
 * - Birebir aynı yapı: Header, Hero, How It Works, Cities, Pricing, Form, Gallery, Footer
 */

const cities = [
  { name: "Las Vegas, USA",   shows: "CES, PACK EXPO, MAGIC",                img: "https://images.pexels.com/photos/36015098/pexels-photo-36015098.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Dubai, UAE",       shows: "Gulfood, Arab Health, GITEX",          img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1600&auto=format&fit=crop" },
  { name: "Munich, Germany",  shows: "BAU, IAA Mobility, IFAT",              img: "https://images.unsplash.com/photo-1680550447099-54c7c8c0d4fe?q=80&w=1600&auto=format&fit=crop" },
  { name: "Istanbul, Turkey", shows: "WIN Eurasia, Building Istanbul, IDEF", img: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=1600&auto=format&fit=crop" },
  { name: "Shanghai, China",  shows: "CIIE, Auto Shanghai, Intertextile",    img: "https://images.pexels.com/photos/35132183/pexels-photo-35132183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Amsterdam, NL",    shows: "ISE, INTERTRAFFIC, METSTRADE",         img: "https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?q=80&w=1600&auto=format&fit=crop" },
];

const categories = [
  { name: "3D Designer",         icon: Monitor,   desc: "Stand tasarımı ve görselleştirme" },
  { name: "Event Coordinator",   icon: Briefcase, desc: "Etkinlik planlama ve yönetim" },
  { name: "Graphic Designer",    icon: Palette,   desc: "Baskı, dijital ve marka tasarımı" },
  { name: "Photographer",        icon: Camera,    desc: "Stand fotoğrafçılığı ve video" },
  { name: "Electrician",         icon: Zap,       desc: "Elektrik kurulum ve aydınlatma" },
  { name: "Project Manager",     icon: Star,      desc: "Süreç ve ekip koordinasyonu" },
];

const gallery = [
  { title: "3D Booth Concept — Dubai",       img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
  { title: "Brand Graphics — Las Vegas",     img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/wly6v2kk_behance_download_1512846870389.jpg" },
  { title: "Stand Photography — Munich",     img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
  { title: "Event Coordination — Shanghai",  img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/elbz7932_Screen%20Shot%202018-10-28%20at%2007.16.31.png" },
  { title: "Lighting Design — Amsterdam",    img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/og4pi04c_d99fbd54082151.594c269fa2921.jpg" },
  { title: "Project Management — Istanbul",  img: "https://customer-assets.emergentagent.com/job_expo-stand/artifacts/1p6p52h6_behance_download_1512683025775.jpg" },
];

export default function FreelancerLanding() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "",
    experience: "",
    location: "",
    daily_rate: "",
    message: "",
    consent: false,
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-white text-slate-900">

      {/* ── HEADER ── */}
      <header className="bg-white px-4 md:px-6 py-4 relative z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg"
              alt="EXPO STAND Logo"
              className="w-10 h-8 md:w-12 md:h-10"
            />
            <div className="text-slate-700">
              <div className="text-lg md:text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-lg md:text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>

          {/* Nav */}
          <div className="hidden md:flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            <div className="relative group">
              <span className="hover:text-red-600 cursor-pointer flex items-center">Sign In <span className="ml-1">▾</span></span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-36 z-50">
                <a href="/exhibitor/login"  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/login"   className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
                <a href="/freelancer/login" className="block px-4 py-2 text-sm text-red-600 font-semibold hover:bg-red-50">Freelancer</a>
                <div className="border-t border-gray-100 my-1" />
                <a href="/admin-login"      className="block px-4 py-2 text-sm text-gray-500 hover:bg-gray-100">Admin Login</a>
              </div>
            </div>
            <div className="relative group">
              <span className="hover:text-red-600 cursor-pointer flex items-center">Sign Up <span className="ml-1">▾</span></span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-36 z-50">
                <a href="/exhibitor/landing"  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Exhibitor</a>
                <a href="/contractor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Contractor</a>
                <a href="/supplier/landing"   className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Supplier</a>
                <a href="/freelancer/landing" className="block px-4 py-2 text-sm text-red-600 font-semibold hover:bg-red-50">Freelancer</a>
              </div>
            </div>
            <span className="hover:text-red-600 cursor-pointer">Contact</span>
          </div>

          {/* Mobile */}
          <a href="/freelancer/login" className="md:hidden px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors">
            Sign In
          </a>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden flex items-center" style={{ height: "calc(100vh - 80px)" }}>
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2400&auto=format&fit=crop"
            alt="Expo hall"
            className="h-full w-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/70 to-white" />
        </div>

        <div className="mx-auto max-w-7xl px-6 w-full pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm shadow-sm mb-2 text-red-700">
                <Sparkles className="h-4 w-4" />
                <span>Stand sektöründe 28+ kategoride iş fırsatı</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl leading-tight -mt-2">
                Yeteneklerini<br />
                <span className="text-red-600">ExpoStand</span><br />
                ile Dünyaya Aç
              </h1>
              <p className="mt-4 text-lg leading-7 text-slate-600">
                Fuar ve etkinlik sektörünün leading platformu. 3D tasarımcıdan elektrikçiye, koordinatörden fotoğrafçıya — profilini oluştur, projeler al, güvenle öde.
              </p>

              <div className="mt-6 space-y-2">
                <p className="text-sm text-slate-700"><strong>🎯 Kaliteli Projeler</strong> — Bütçesi ve takvimi net, doğrulanmış müşterilerle çalış</p>
                <p className="text-sm text-slate-700"><strong>📋 Net Briefler</strong> — Proje detaylarını önceden gör, sürpriz yok</p>
                <p className="text-sm text-slate-700"><strong>🌍 Global Erişim</strong> — Las Vegas'tan Dubai'ye dünya genelinde iş fırsatları</p>
                <p className="text-sm text-slate-700"><strong>💰 Güvenli Ödeme</strong> — Onaylanan teslimattan sonra garanti ödeme</p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="/freelancer/signup"
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-white shadow hover:bg-red-700 transition-colors"
                >
                  Ücretsiz Kaydol <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="/find-freelancers"
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-slate-900 shadow-sm hover:bg-slate-50 transition-colors"
                >
                  Freelancer Dizini <Users className="h-4 w-4" />
                </a>
              </div>

              <ul className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600"/> Ücretsiz profil</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600"/> Hızlı onay</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-red-600"/> Güvenli ödeme</li>
              </ul>
            </motion.div>

            {/* Sağ görsel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=600&auto=format&fit=crop"
                  alt="Freelancer working"
                  className="w-full h-[300px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-800">
                      Stand sektöründe uzman freelancer'lar için tek platform
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              <div className="absolute -top-2 -right-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">28+</div>
                  <div className="text-xs text-slate-500">Kategori</div>
                </div>
              </div>
              <div className="absolute top-1/2 -left-2 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">46</div>
                  <div className="text-xs text-slate-500">Ülke</div>
                </div>
              </div>
              <div className="absolute -bottom-2 left-1/4 bg-white rounded-lg shadow-lg p-2 border border-slate-100">
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">%100</div>
                  <div className="text-xs text-slate-500">Ücretsiz</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── KATEGORILER ── */}
      <section className="bg-red-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Hangi Kategoride Uzman Olursun?</h2>
            <p className="mt-2 text-slate-600 mb-4">28'den fazla kategoride profilini oluştur, müşterilere kendini göster.</p>
            <p className="text-slate-700">
              Fuar ve etkinlik sektörü yüzlerce farklı uzmanlık alanı barındırır. ExpoStand, hem teknik hem yaratif freelancer'lara kapısını açar.
              <span className="font-medium text-red-600"> Ortalama proje değeri $850 | Ortalama teslim süresi 7 gün.</span>
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {categories.map(({ name, icon: Icon, desc }) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-red-200 transition-colors"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-red-50" />
                <Icon className="mb-4 h-8 w-8 text-red-600" />
                <h3 className="text-xl font-semibold">{name}</h3>
                <p className="mt-2 text-slate-600">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NASIL ÇALIŞIR ── */}
      <section className="bg-slate-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">3 Adımda İş Almaya Başla</h2>
            <p className="mt-2 text-slate-600">Kayıttan ilk projeye kadar kolay ve hızlı süreç.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Profilini Oluştur",
                desc: "Kategorini seç, yeteneklerini yaz, portfolyondan max 6 proje yükle. Dakikalar içinde profilin canlıya geçer.",
                Icon: Users,
                badge: "Adım 1",
              },
              {
                title: "Teklif Al veya Ver",
                desc: "Firma tekliflerini bekle ya da açık iş ilanlarına başvur. Net briefler, şeffaf bütçe, zaman kaybı yok.",
                Icon: Briefcase,
                badge: "Adım 2",
              },
              {
                title: "Teslim Et, Öde",
                desc: "İşi teslim et, güvenli ödeme sistemimizle anında tahsilat yap. Puanını yükselt, premium fırsatlara ulaş.",
                Icon: CheckCircle2,
                badge: "Adım 3",
              },
            ].map(({ title, desc, Icon, badge }) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-red-50" />
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">{badge}</div>
                <Icon className="mb-4 h-8 w-8 text-red-600" />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-2 text-slate-600">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ŞEHIRLER ── */}
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Dünyanın Her Yerinden İş</h2>
              <p className="mt-2 text-slate-600">46 ülkedeki fuar ve etkinlik projeleri seni bekliyor.</p>
            </div>
            <a href="/find-freelancers" className="inline-flex items-center gap-2 text-red-700 hover:underline">
              Freelancer Dizini <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {cities.map((c) => (
              <div key={c.name} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-red-200 transition-colors">
                <div className="relative h-40 w-full overflow-hidden">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{c.shows}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FİYATLANDIRMA ── */}
      <section className="bg-slate-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Üyelik Planları</h2>
            <p className="mt-2 text-slate-600">Freelancer kariyerinin aşamasına göre planını seç.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                name: "Ücretsiz",
                price: "$0",
                period: "/ay",
                features: ["Public profil sayfası", "Max 2 portfolyo projesi", "Kategori dizininde görünüm", "Standart mesajlaşma", "Topluluk desteği"],
                cta: "Hemen Kaydol",
              },
              {
                name: "Professional",
                price: "$49",
                period: "/ay",
                features: ["Max 6 portfolyo projesi", "Öncelikli proje bildirimi", "Doğrulanmış rozet", "Gelişmiş arama sıralaması", "Analitik paneli", "Öncelikli destek"],
                cta: "Ücretsiz Dene",
                highlighted: true,
              },
              {
                name: "Enterprise",
                price: "Özel",
                period: "",
                features: ["Özel kategori sayfası", "Takım yönetimi", "Özel sözleşme şablonları", "Hesap yöneticisi", "API entegrasyonları"],
                cta: "Satış ile Görüş",
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`flex h-full flex-col rounded-2xl border bg-white p-6 shadow-sm ${
                  p.highlighted ? "border-red-600 ring-2 ring-red-100" : "border-slate-200"
                }`}
              >
                <div className="mb-4 flex items-baseline justify-between">
                  <h3 className="text-xl font-semibold">{p.name}</h3>
                  {p.highlighted && (
                    <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">Popüler</span>
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{p.price}</span>
                  <span className="text-slate-600">{p.period}</span>
                </div>
                <ul className="mb-6 space-y-2 text-slate-700">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 text-red-600 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={p.name === "Enterprise" ? "#/contact" : "#/freelancer/signup"}
                  className={`mt-auto inline-flex items-center justify-center rounded-2xl px-4 py-2 font-medium shadow-sm ${
                    p.highlighted ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {p.cta}
                </a>
                <p className="mt-3 text-xs text-slate-500">Başarılı işler için komisyon uygulanır.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Hemen Başvur — 24 Saatte Onay</h2>
            <p className="mt-2 text-slate-600">Bize yeteneklerini anlat, profil kurulumunu hemen halledelim.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <form className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Ad Soyad"     placeholder="Örn. Ayşe Kaya"         value={form.name}       onChange={(v) => update("name", v)}       Icon={Users} />
                <Field label="E-posta"      placeholder="sen@email.com"           value={form.email}      onChange={(v) => update("email", v)}      Icon={Mail}  type="email" />
                <Field label="Kategori"     placeholder="Örn. 3D Designer"        value={form.category}   onChange={(v) => update("category", v)}   Icon={Briefcase} />
                <Field label="Deneyim"      placeholder="Örn. 3 yıl"              value={form.experience} onChange={(v) => update("experience", v)} Icon={Clock} />
                <Field label="Konum"        placeholder="Örn. İstanbul, Dubai"    value={form.location}   onChange={(v) => update("location", v)}   Icon={MapPin} />
                <Field label="Günlük Ücret" placeholder="Örn. 200 USD"            value={form.daily_rate} onChange={(v) => update("daily_rate", v)} Icon={DollarSign} />
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">Yeteneklerin ve Hizmetlerin</label>
                <textarea
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-red-600"
                  rows={4}
                  placeholder="Hangi yazılımları kullanıyorsun? Tipik proje büyüklüğün nedir?"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} />
                Freelancer ağına katılmak istiyorum.
              </label>
              <a
                href="/freelancer/signup"
                className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 font-medium text-white shadow hover:bg-red-700 transition-colors"
              >
                Profil Oluştur <ArrowRight className="h-4 w-4" />
              </a>
            </form>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">Profil Onayı İçin Gerekenler</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
                <li>Kategori bazlı uzmanlık kanıtı veya portfolyo</li>
                <li>Gerçek ad-soyad ve iletişim bilgileri</li>
                <li>En az 1 tamamlanmış proje referansı</li>
                <li>Beklenen proje türleri ve kapasite</li>
                <li>Dil becerileri ve çalışma saatleri</li>
                <li>İstenirse serbest çalışma sözleşmesi şablonu</li>
              </ul>
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p className="flex items-start gap-2">
                  <ShieldCheck className="mt-0.5 h-5 w-5 text-red-600 shrink-0" />
                  Tüm freelancer'lar profil doğrulamasından geçer. Müşteriler yalnızca onaylı profilleri görebilir.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTİMONIALLAR ── */}
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight">Freelancer'lar Ne Diyor?</h2>
            <p className="mt-2 text-slate-600">Ağımızdaki profesyonellerin gerçek deneyimleri.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                name: "Burak Yılmaz",
                cat: "3D Designer · İstanbul",
                avatar: "BY",
                rating: 5,
                projects: 18,
                quote: "ExpoStand sayesinde Dubai, Frankfurt ve Las Vegas'taki fuarlara uzaktan tasarım yaptım. Platform sayesinde müşteri bulmak çok kolaylaştı.",
              },
              {
                name: "Elif Şahin",
                cat: "Event Coordinator · İzmir",
                avatar: "EŞ",
                rating: 5,
                projects: 11,
                quote: "Günde 2-3 teklif geliyor. Daha önce yalnızca yerel işler alırken şimdi uluslararası projelerde çalışıyorum. Komisyon sistemi de son derece şeffaf.",
              },
              {
                name: "Mehmet Arslan",
                cat: "Graphic Designer · Ankara",
                avatar: "MA",
                rating: 4,
                projects: 24,
                quote: "Portfolyo sayfam canlıya geçtikten bir hafta sonra 3 farklı firmadan iş teklifi aldım. Profil yönetimi çok basit.",
              },
            ].map(({ name, cat, avatar, rating, projects, quote }) => (
              <div key={name} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 text-red-700 font-black text-sm flex items-center justify-center shrink-0">
                    {avatar}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{name}</p>
                    <p className="text-xs text-slate-500">{cat}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                      ))}
                      <span className="text-xs text-slate-400 ml-1">{projects} proje</span>
                    </div>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">"{quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PORTFÖLYOdan İLHAM AL ── */}
      <section className="bg-slate-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-3xl font-semibold tracking-tight">Portfolyonla Öne Çık</h2>
              <p className="mt-2 text-slate-600">Ağımızdaki freelancer'ların gerçek projeleri.</p>
            </div>
            <a href="/find-freelancers" className="inline-flex items-center gap-2 text-red-700 hover:underline">
              Tüm Freelancer'ları Gör <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {gallery.map((g) => (
              <div key={g.title} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-red-200 transition-colors">
                <div className="relative h-44 w-full overflow-hidden">
                  <img src={g.img} alt={g.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-slate-900">{g.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="grid gap-8 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 text-xl font-bold">
                <Images className="h-6 w-6 text-red-600" />
                ExpoStand
              </div>
              <p className="mt-2 max-w-md text-sm text-slate-600">Stand sektöründe freelancer'ları kaliteli projelerle buluşturuyoruz.</p>
              <p className="mt-4 text-xs text-slate-500">© 2026 ExpoStand. Tüm hakları saklıdır.</p>
            </div>
            <nav className="grid grid-cols-2 gap-8 md:col-span-3 md:grid-cols-3">
              <div>
                <h4 className="text-sm font-semibold">Freelancer'lar İçin</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/freelancer/signup"   className="hover:underline hover:text-red-600">Kaydol</a></li>
                  <li><a href="/find-freelancers"    className="hover:underline hover:text-red-600">Dizine Gözat</a></li>
                  <li><a href="/freelancer/login"    className="hover:underline hover:text-red-600">Giriş Yap</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Kaynaklar</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/pricing"  className="hover:underline">Fiyatlandırma</a></li>
                  <li><a href="/support" className="hover:underline">Destek</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold">Şirket</h4>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li><a href="/about"         className="hover:underline">Hakkında</a></li>
                  <li><a href="/legal/terms"   className="hover:underline">Kullanım Şartları</a></li>
                  <li><a href="/legal/privacy" className="hover:underline">Gizlilik</a></li>
                </ul>
              </div>
            </nav>
          </div>
          <p className="mt-6 text-xs text-slate-500">ExpoStand doğrulanmış freelancer'ları fuar ve etkinlik projelerine bağlar. Tüm ortaklıklar doğrulama ve kalite standartlarına tabidir.</p>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", Icon }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-red-600">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        <input
          type={type}
          className="w-full bg-transparent outline-none placeholder:text-slate-400"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}
